import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

export interface BugReport {
  id: string
  account_id: string
  reporter_email: string
  category: string
  severity: string
  title: string
  description: string
  screenshot_key: string | null
  status: string
  rewarded: boolean
  admin_notes: string | null
  created_at: string
  updated_at: string
  resolved_at: string | null
}

interface BugReportListResponse {
  data: BugReport[]
  total: number
  page: number
  per_page: number
}

interface BugReportListParams {
  page?: number
  per_page?: number
  status?: string
  category?: string
}

interface UpdateBugReportPayload {
  id: string
  status?: string
  admin_notes?: string
  rewarded?: boolean
}

const BUG_REPORTS_KEY = ['admin-bug-reports'] as const

export function useAdminBugReports(
  params?: BugReportListParams,
): ReturnType<typeof useQuery<BugReportListResponse>> {
  return useQuery({
    queryKey: [...BUG_REPORTS_KEY, params],
    queryFn: () =>
      apiClient.get<BugReportListResponse>(
        endpoints.bugReports.list({
          page: params?.page,
          per_page: params?.per_page,
          status: params?.status,
          category: params?.category,
        }),
      ),
  })
}

export function useUpdateBugReport(): ReturnType<
  typeof useMutation<BugReport, unknown, UpdateBugReportPayload>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateBugReportPayload) =>
      apiClient.patch<BugReport>(endpoints.bugReports.update(id), body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BUG_REPORTS_KEY })
    },
  })
}
